// src/services/protocolExtractionService.js
//
// Phase 9 — client data layer for the Protocol → SOA extraction pipeline.
//
// Responsibilities (all site-scoped via firestorePaths):
//   - upload a protocol PDF to Storage + create the protocol Firestore doc
//   - kick off / retry server-side extraction (authenticated Cloud Function)
//   - read protocols + the working extraction for the review UI
//   - persist reviewer edits, and approve (server generates source docs)
//
// The heavy lifting (render, detect, OCR, vision-extract, merge, source-doc
// generation) runs server-side in the extractProtocolSoa / approveProtocolSoa
// Cloud Functions — this file only orchestrates and reads/writes Firestore.

import { db, storage, auth } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getActiveTenantId, getActiveSiteId } from "./activeTenant";
import {
  getStudyProtocolsPath,
  getStudyProtocolDocPath,
  getProtocolExtractionDocPath,
} from "./firestorePaths";
import { newProtocolDoc, PROTOCOL_STATUS } from "./protocolTypes";

const FN_BASE = `${FUNCTIONS_BASE}`;

const lc = (s) => String(s || "").toLowerCase();
const sanitize = (name) => String(name || "file.pdf").replace(/[^\w.\-]/g, "_");

// Authenticated POST to a Cloud Function (sends the Firebase ID token so the
// server can verify the caller AND their site membership before doing anything).
const callFn = async (name, body) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const idToken = await user.getIdToken();
  const resp = await fetch(`${FN_BASE}/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body || {}),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data.error || `${name} failed (${resp.status}).`);
  }
  return data;
};

/**
 * Upload a protocol PDF and create its Firestore doc (status "uploaded").
 * Storage is namespaced by tenant/site/study/protocol so it mirrors the
 * Firestore isolation and stays organized.
 * @returns {Promise<{ protocolId: string, storagePath: string, downloadUrl: string }>}
 */
export const uploadAndCreateProtocol = async (studyId, file) => {
  if (!studyId) throw new Error("studyId is required.");
  if (!file) throw new Error("No file selected.");

  const tenantId = getActiveTenantId();
  const siteId = getActiveSiteId();

  // Reserve the protocol id up front so the Storage path is stable/traceable.
  const protocolRef = doc(collection(db, getStudyProtocolsPath(studyId)));
  const protocolId = protocolRef.id;

  const storagePath = `protocols/${tenantId}/${siteId}/${studyId}/${protocolId}/original_${sanitize(
    file.name
  )}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, { contentType: file.type || "application/pdf" });
  const downloadUrl = await getDownloadURL(storageRef);

  await setDoc(protocolRef, {
    ...newProtocolDoc({ fileName: file.name, storagePath, studyId }),
    downloadUrl,
    tenantId,
    siteId,
    createdBy: lc(auth.currentUser?.email),
    uploadedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });

  return { protocolId, storagePath, downloadUrl };
};

/**
 * Start (or retry) server-side extraction for a protocol. Idempotent-ish: the
 * server resets status to "processing" and re-runs the pipeline.
 */
export const startExtraction = async (studyId, protocolId) =>
  callFn("extractProtocolSoa", {
    studyId,
    protocolId,
    tenantId: getActiveTenantId(),
    siteId: getActiveSiteId(),
  });

// Failed/needs-review extractions can be re-run with the same call.
export const retryExtraction = startExtraction;

/** Live list of a study's protocols, newest first. Returns an unsubscribe fn. */
export const listenProtocols = (studyId, onData, onError) => {
  const q = query(
    collection(db, getStudyProtocolsPath(studyId)),
    orderBy("uploadedAt", "desc")
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error("listenProtocols error:", err);
      onError && onError(err);
    }
  );
};

/**
 * A downloadable URL for the study's protocol PDF, or "" if it has none.
 *
 * A study can arrive at "has a protocol" by TWO different roads, and they store
 * it in two different places:
 *
 *   1. GenerateTemplateModal — uploads the PDF, generates a template from it,
 *      and stamps `protocolUrl` onto the TEMPLATE document.
 *   2. ProtocolsPage (the Phase 9 pipeline) — uploads it as its own document at
 *      `studies/{studyId}/protocols/{protocolId}`, extracts the SoA, and gets
 *      approved. This road never touches `protocolUrl`.
 *
 * Anything that asks "does this study have a protocol?" has to look in both, or
 * it tells a coordinator staring at an approved protocol that there isn't one.
 *
 * Ordering, when a study has several: the newest APPROVED protocol wins, then
 * the newest that has a file at all. An approved protocol is the one the site
 * has actually reviewed, so it beats a later half-finished upload — but a study
 * mid-review still gets its file rather than nothing.
 *
 * Sorted in memory rather than with orderBy("uploadedAt"): a Firestore query
 * that orders by a field SILENTLY OMITS every document missing it, so one
 * protocol written before that field existed would make this return "" while
 * the UI right beside it lists the protocol.
 *
 * @param {string} studyId  the real study id — NOT a `{studyId}__assembled`
 *   template id. Callers holding a template id must pass it through
 *   exportStudyIdFor first.
 * @returns {Promise<string>}
 */
export const findProtocolUrl = async (studyId) => {
  if (!studyId) return "";
  let docs = [];
  try {
    const snap = await getDocs(collection(db, getStudyProtocolsPath(studyId)));
    docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    // A study with no protocols subcollection, or a read the rules refuse, is
    // "no protocol" — not a crash in whatever asked.
    console.warn("findProtocolUrl:", e.message);
    return "";
  }
  const withFile = docs.filter((p) => p && p.downloadUrl);
  if (!withFile.length) return "";
  const ms = (p) => p.uploadedAt?.toMillis?.() ?? p.createdAt?.toMillis?.() ?? 0;
  const newestFirst = (a, b) => ms(b) - ms(a);
  const approved = withFile
    .filter((p) => p.status === PROTOCOL_STATUS.APPROVED)
    .sort(newestFirst);
  return (approved[0] || withFile.sort(newestFirst)[0]).downloadUrl || "";
};

export const getProtocol = async (studyId, protocolId) => {
  const snap = await getDoc(doc(db, getStudyProtocolDocPath(studyId, protocolId)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/** The working (reviewable) merged SOA extraction, or null if not yet produced. */
export const getExtraction = async (studyId, protocolId) => {
  const snap = await getDoc(
    doc(db, getProtocolExtractionDocPath(studyId, protocolId))
  );
  return snap.exists() ? snap.data() : null;
};

export const listenExtraction = (studyId, protocolId, onData, onError) =>
  onSnapshot(
    doc(db, getProtocolExtractionDocPath(studyId, protocolId)),
    (snap) => onData(snap.exists() ? snap.data() : null),
    (err) => {
      console.error("listenExtraction error:", err);
      onError && onError(err);
    }
  );

/**
 * Persist reviewer edits to the working extraction. Stays in "needs_review";
 * approval is a separate, explicit step.
 */
export const saveExtractionEdits = async (studyId, protocolId, extraction) => {
  await updateDoc(doc(db, getProtocolExtractionDocPath(studyId, protocolId)), {
    visits: extraction.visits || [],
    assessments: extraction.assessments || [],
    footnotes: extraction.footnotes || [],
    warnings: extraction.warnings || [],
    status: PROTOCOL_STATUS.NEEDS_REVIEW,
    editedAt: serverTimestamp(),
    editedBy: lc(auth.currentUser?.email),
  });
};

/**
 * Approve the extraction. The server freezes an approvedSoa snapshot, generates
 * per-visit source docs, and flips the protocol status to "approved". Done
 * server-side so source-doc generation is consistent and auditable.
 */
export const approveExtraction = async (studyId, protocolId) =>
  callFn("approveProtocolSoa", {
    studyId,
    protocolId,
    tenantId: getActiveTenantId(),
    siteId: getActiveSiteId(),
  });

/**
 * Ask a question about a study's visits/assessments. Answered server-side from
 * the APPROVED per-visit source docs only (with citations) — never the raw PDF.
 * @returns {Promise<{answer:string, citations:Object[], usedDocs:number}>}
 */
export const askSourceDocs = async (studyId, question) =>
  callFn("querySourceDocs", {
    studyId,
    question,
    tenantId: getActiveTenantId(),
    siteId: getActiveSiteId(),
  });
